
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  FormFooter,
  OptionType,
  TagerFormSubmitEvent,
  useSelectOptionsResource,
  FormFieldComboBox,
  FormField,
} from "@tager/admin-ui";
import { getNameWithDepth } from "@tager/admin-services";
import { Page } from "@tager/admin-layout";

import { CategoryType, FullProductTypeType } from "@/typings/model";
import {
  createProductType,
  getCategoryList,
  getProductType,
  updateProductType,
} from "@/services/requests";
import { getProductTypeFormUrl, getProductTypeListUrl } from "@/utils/paths";

import {
  convertFormValuesToProductTypeCreationPayload,
  convertFormValuesToProductTypeUpdatePayload,
  convertProductTypeToFormValues,
  FormValues,
} from "./ProductTypeForm.helpers";

export default defineComponent({
  name: "ProductTypeForm",
  components: { FormFooter, Page, FormField, FormFieldComboBox },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const productTypeId = computed<string>(
      () => route.params.productTypeId as string
    );

    const isCreation = computed<boolean>(
      () => productTypeId.value === "create"
    );

    const [
      fetchProductType,
      { data: productType, loading: isProductTypeLoading },
    ] = useResource<Nullable<FullProductTypeType>>({
      fetchResource: () => getProductType(productTypeId.value),
      initialValue: null,
      resourceName: "productType",
    });

    const {
      loading: isCategoryListLoading,
      options: categoryOptionList,
      noOptionsMessage: noProductOptionsMessage,
      handleSearchQueryChange: handleProductSearchQueryChange,
      fetchEntityList: fetchCategoryList,
    } = useSelectOptionsResource<CategoryType, OptionType<number>>({
      fetchEntityList: getCategoryList,
      resourceName: "Category list",
      minQueryLength: 0,
      convertEntityToOption: (category) => ({
        value: category.id,
        label: getNameWithDepth(category.name, category.depth),
      }),
    });

    onMounted(() => {
      fetchCategoryList({ query: "" });
      if (isCreation.value) return;
      fetchProductType();
    });

    const values = ref<FormValues>(
      convertProductTypeToFormValues(
        productType.value,
        categoryOptionList.value
      )
    );

    function setValues() {
      values.value = convertProductTypeToFormValues(
        productType.value,
        categoryOptionList.value
      );
    }

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(productType, () => {
      setValues();
    });

    watch(categoryOptionList, () => {
      setValues();
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToProductTypeCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToProductTypeUpdatePayload(
        values.value
      );
      const requestPromise = isCreation.value
        ? createProductType(creationBody)
        : updateProductType(productTypeId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(
              getProductTypeFormUrl({ productTypeId: response.data.id })
            );
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getProductTypeListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertProductTypeToFormValues(
              null,
              categoryOptionList.value
            );
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Тип успешно создан`
              : "Тип успешно обновлен",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при создании типа`
              : "Ошибка при обновлении типа",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(
      () => isProductTypeLoading.value || isCategoryListLoading.value
    );

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Создать тип товара`
        : `Редактирование типа товара "${values.value.name}"`;
    });

    return {
      backButtonUrl: getProductTypeListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      productType,
      isCreation,
      pageTitle,
      categoryOptionList,
    };
  },
});
